import React from "react";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../components/TitleWithDescription/TitleWithDescription";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import Title from "../../components/Title/Title";
import { useLanguage } from "../../context/LanguageContext";
import { getClockworkAtomicTranslations } from "../../translations/clockworkAtomicTemplate";
import { isMobile } from "../../utils/appUtils";
import { isMobileOrTablet } from "../../utils/appUtils";
import { useEditMode } from "../../context/EditModeContext";
import EditableText from "../../components/EditableText/EditableText";
import { usePageContent } from "../../hooks/usePageContent";
import { clockworkAtomicTranslations } from "../../translations/clockworkAtomicTemplate";

function ClockworkAtomic() {
    const { language } = useLanguage();
    const { isEditMode } = useEditMode();
    const { content, loading } = usePageContent<typeof clockworkAtomicTranslations>('clockwork-atomic');

    // Use translations as fallback content
    const t = content || getClockworkAtomicTranslations(language);

    // Show loading state if needed
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="">
            <BackgroundCover
                backgroundHeight={isMobileOrTablet() ? (isMobile() ? 400 : 600) : undefined}
                imagePosition={isMobileOrTablet() ? (isMobile() ? "initial" : "center") : "initial"}
                backgroundImage="/images/atomic_clock_background.jpg"
            >
                <div className="absolute top-1/2 left-[10%] -translate-y-1/2 z-10 hidden md:block">
                    <Title
                        firstLine={isEditMode ? (
                            <EditableText
                                contentKey="clockwork-atomic.title.firstLine"
                                defaultValue={t.title.firstLine}
                            />
                        ) : t.title.firstLine}
                        secondLine={isEditMode ? (
                            <EditableText
                                contentKey="clockwork-atomic.title.secondLine"
                                defaultValue={t.title.secondLine}
                            />
                        ) : t.title.secondLine}
                        className="text-white hidden md:block"
                        animate={true}
                    />
                </div>
            </BackgroundCover>

            <div className="mx-auto px-4 py-12 md:py-24">
                <div className="block md:hidden mb-8">
                    <Title
                        firstLine={isEditMode ? (
                            <EditableText
                                contentKey="clockwork-atomic.title.firstLine"
                                defaultValue={t.title.firstLine}
                            />
                        ) : t.title.firstLine}
                        secondLine={isEditMode ? (
                            <EditableText
                                contentKey="clockwork-atomic.title.secondLine"
                                defaultValue={t.title.secondLine}
                            />
                        ) : t.title.secondLine}
                        className=""
                        animate={true}
                    />
                </div>
                <div className="flex flex-col items-center">
                    <div className="max-w-4xl text-sm text-gray-600 leading-relaxed">
                        {isEditMode ? (
                            <EditableText
                                contentKey="clockwork-atomic.introduction"
                                defaultValue={t.introduction}
                                multiline={true}
                            />
                        ) : (
                            <p>{t.introduction}</p>
                        )}
                        <ul className="list-disc pl-6 mt-4 space-y-2">
                            {t.bulletPoints.map((point: string, index: number) => (
                                <li key={index}>
                                    {isEditMode ? (
                                        <EditableText
                                            contentKey={`clockwork-atomic.bulletPoints.${index}`}
                                            defaultValue={point}
                                        />
                                    ) : point}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <div className="relative w-full overflow-hidden">
                <div className="w-full bg-gradient-to-b from-[#E1D8D3] via-[#FFFFFF] to-[#E1D8D3] py-12">
                    <div className="mx-auto px-4 md:px-12 pt-8 space-y-12 max-w-7xl">
                        <TimelineSection
                            title={{
                                firstLine: isEditMode ? (
                                    <EditableText
                                        contentKey="clockwork-atomic.sections.understanding.title.firstLine"
                                        defaultValue={t.sections.understanding.title.firstLine}
                                    />
                                ) : t.sections.understanding.title.firstLine,
                                secondLine: isEditMode ? (
                                    <EditableText
                                        contentKey="clockwork-atomic.sections.understanding.title.secondLine"
                                        defaultValue={t.sections.understanding.title.secondLine}
                                    />
                                ) : t.sections.understanding.title.secondLine,
                            }}
                            description={isEditMode ? (
                                <EditableText
                                    contentKey="clockwork-atomic.sections.understanding.description"
                                    defaultValue={t.sections.understanding.description}
                                    multiline={true}
                                />
                            ) : t.sections.understanding.description}
                            media={{
                                type: 'image',
                                src: "/images/atomic_clock_sub1.jpg",
                                alt: "Understanding Time"
                            }}
                            mediaPosition="right"
                        />

                        <TimelineSection
                            title={{
                                firstLine: isEditMode ? (
                                    <EditableText
                                        contentKey="clockwork-atomic.sections.experiments.title.firstLine"
                                        defaultValue={t.sections.experiments.title.firstLine}
                                    />
                                ) : t.sections.experiments.title.firstLine,
                                secondLine: isEditMode ? (
                                    <EditableText
                                        contentKey="clockwork-atomic.sections.experiments.title.secondLine"
                                        defaultValue={t.sections.experiments.title.secondLine}
                                    />
                                ) : t.sections.experiments.title.secondLine,
                            }}
                            description={isEditMode ? (
                                <EditableText
                                    contentKey="clockwork-atomic.sections.experiments.description"
                                    defaultValue={t.sections.experiments.description}
                                    multiline={true}
                                />
                            ) : t.sections.experiments.description}
                            media={{
                                type: 'image',
                                src: "/images/atomic_clock_sub2.jpg",
                                alt: "Time Dilation Experiment Part 1"
                            }}
                            mediaPosition="left"
                        />

                        <TimelineSection
                            title={{
                                firstLine: isEditMode ? (
                                    <EditableText
                                        contentKey="clockwork-atomic.sections.measuring.title.firstLine"
                                        defaultValue={t.sections.measuring.title.firstLine}
                                    />
                                ) : t.sections.measuring.title.firstLine,
                                secondLine: isEditMode ? (
                                    <EditableText
                                        contentKey="clockwork-atomic.sections.measuring.title.secondLine"
                                        defaultValue={t.sections.measuring.title.secondLine}
                                    />
                                ) : t.sections.measuring.title.secondLine,
                            }}
                            description={isEditMode ? (
                                <EditableText
                                    contentKey="clockwork-atomic.sections.measuring.description"
                                    defaultValue={t.sections.measuring.description}
                                    multiline={true}
                                />
                            ) : t.sections.measuring.description}
                            media={{
                                type: 'image',
                                src: "/images/atomic_clock_sub3.jpg",
                                alt: "Time Dilation Experiment Part 2"
                            }}
                            mediaPosition="right"
                        />

                        <TimelineSection
                            title={{
                                firstLine: isEditMode ? (
                                    <EditableText
                                        contentKey="clockwork-atomic.sections.precision.title.firstLine"
                                        defaultValue={t.sections.precision.title.firstLine}
                                    />
                                ) : t.sections.precision.title.firstLine,
                                secondLine: isEditMode ? (
                                    <EditableText
                                        contentKey="clockwork-atomic.sections.precision.title.secondLine"
                                        defaultValue={t.sections.precision.title.secondLine}
                                    />
                                ) : t.sections.precision.title.secondLine,
                            }}
                            description={isEditMode ? (
                                <EditableText
                                    contentKey="clockwork-atomic.sections.precision.description"
                                    defaultValue={t.sections.precision.description}
                                    multiline={true}
                                />
                            ) : t.sections.precision.description}
                            media={{
                                type: 'image',
                                src: "/images/atomic_clock_sub4.jpg",
                                alt: "Atomic Clock Precision"
                            }}
                            mediaPosition="left"
                            showLine={false}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ClockworkAtomic; 